import { openShell, confirmDialog } from "./dialogs.ts";
import { CANVAS_SVG, DOC_SVG, FOLDER_SVG } from "./glyphs.ts";
import { TRASH_DIR } from "../vault/trash.ts";
import { type VaultAdapter } from "../vault/VaultAdapter.ts";

/**
 * La papelera del vault, con Restaurar y Borrar definitivo.
 *
 * Vive en su propio módulo y no en `dialogs.ts` porque es el único modal que
 * sabe qué es un vault: los de allá son genéricos (pedir un nombre, confirmar)
 * y no tienen por qué conocer el adaptador. Comparte el mismo `openShell`, así
 * que sigue siendo la misma ventana que el resto.
 *
 * La carpeta `Trash/` existe en el disco a la vista, sin punto adelante: esto
 * es la comodidad, no el único acceso. Si la app no abre, arrastrar la carpeta
 * de vuelta en Finder hace exactamente lo mismo que el botón Restore.
 */
export function openTrashDialog(
  adapter: VaultAdapter,
  onChanged: () => Promise<void>,
): Promise<void> {
  return new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      h.close();
      resolve();
    };
    const h = openShell(finish);
    h.dialog.classList.add("trash-dialog");

    h.dialog.innerHTML = `
      <h2 class="dialog-title">Trash</h2>
      <p class="dialog-msg"></p>
      <div class="trash-list" role="list"></div>
      <div class="dialog-actions">
        <button type="button" class="btn-ghost push-left" data-act="empty">Empty trash</button>
        <button type="button" class="btn-primary" data-act="done">Done</button>
      </div>`;

    const msg = h.dialog.querySelector<HTMLParagraphElement>(".dialog-msg")!;
    const list = h.dialog.querySelector<HTMLDivElement>(".trash-list")!;
    const emptyBtn = h.dialog.querySelector<HTMLButtonElement>('[data-act="empty"]')!;
    msg.textContent = `Deleted files are kept in the "${TRASH_DIR}" folder of your vault. Nothing leaves it until you empty it here or in Finder.`;
    h.dialog.querySelector('[data-act="done"]')!.addEventListener("click", finish);

    emptyBtn.addEventListener("click", () => {
      void (async () => {
        const ok = await confirmDialog({
          title: "Empty the trash?",
          message: "Everything in it is deleted for good. This can't be undone.",
          okLabel: "Empty trash",
          danger: true,
        });
        if (!ok) return;
        await adapter.trashEmpty();
        await render();
      })();
    });

    /** Rebuild the list from disk. Called after every action, so a restore that
     *  quietly failed can't leave a row claiming otherwise. */
    async function render(): Promise<void> {
      const entries = await adapter.trashList();
      list.replaceChildren();
      emptyBtn.disabled = entries.length === 0;

      if (entries.length === 0) {
        const empty = document.createElement("p");
        empty.className = "trash-empty";
        empty.textContent = "Nothing deleted yet.";
        list.appendChild(empty);
        return;
      }

      for (const entry of entries) {
        const row = document.createElement("div");
        row.className = "trash-row";
        row.setAttribute("role", "listitem");

        const icon = document.createElement("span");
        icon.className = "trash-icon";
        icon.innerHTML = entry.kind === "folder" ? FOLDER_SVG : glyphFor(entry.name);
        row.appendChild(icon);

        const text = document.createElement("div");
        text.className = "trash-text";
        const name = document.createElement("span");
        name.className = "trash-name";
        name.textContent = entry.name;
        const meta = document.createElement("span");
        meta.className = "trash-meta";
        meta.textContent = `${originLabel(entry.path, entry.name)} · ${whenLabel(entry.deletedAt)}`;
        text.append(name, meta);
        row.appendChild(text);

        const restore = document.createElement("button");
        restore.type = "button";
        restore.className = "trash-act";
        restore.textContent = "Restore";
        restore.addEventListener("click", () => void act(() => restoreOne(entry.id, entry.name)));

        const forget = document.createElement("button");
        forget.type = "button";
        forget.className = "trash-act is-danger";
        forget.textContent = "Delete";
        forget.addEventListener("click", () =>
          void act(async () => {
            const ok = await confirmDialog({
              title: `Delete "${entry.name}" for good?`,
              message: "It leaves the trash and there is no other copy. This can't be undone.",
              okLabel: "Delete",
              danger: true,
            });
            if (ok) await adapter.trashPurge(entry.id);
          }),
        );

        row.append(restore, forget);
        list.appendChild(row);
      }
    }

    /** Run one row action, then re-read the trash and the tree either way: a
     *  failure still changed the world often enough to redraw. */
    async function act(run: () => Promise<void>): Promise<void> {
      try {
        await run();
      } catch (err) {
        await confirmDialog({
          title: "Couldn't do that",
          message: err instanceof Error ? err.message : String(err),
          okLabel: "Got it",
        });
      }
      await render();
      await onChanged();
    }

    async function restoreOne(id: string, name: string): Promise<void> {
      const landed = await adapter.trashRestore(id);
      const finalName = landed.split("/").pop()!;
      if (finalName === name) return;
      // The old name was taken again while the entry sat in the trash. Saying so
      // beats letting the user hunt for a file that came back under a new name.
      await confirmDialog({
        title: "Restored under a different name",
        message: `"${name}" already exists again, so it came back as "${finalName}".`,
        okLabel: "Got it",
      });
    }

    void render();
  });
}

function glyphFor(name: string): string {
  return name.toLowerCase().endsWith(".canvas") ? CANVAS_SVG : DOC_SVG;
}

/** "SISTEMA/notas/idea.md" → "in SISTEMA/notas"; at the root, "in the vault". */
function originLabel(path: string, name: string): string {
  const dir = path.slice(0, Math.max(0, path.length - name.length - 1));
  return dir ? `in ${dir}` : "in the vault";
}

function whenLabel(deletedAt: number): string {
  const mins = Math.round((Date.now() - deletedAt) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(deletedAt).toLocaleDateString();
}
